<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const uiFlags = computed(() => getters['kanbans/getUIFlags'].value);

const state = reactive({
  name: props.selectedResponse.name || '',
  description: props.selectedResponse.description || '',
});

watch(
  () => props.selectedResponse,
  value => {
    state.name = value.name || '';
    state.description = value.description || '';
  }
);

const rules = {
  name: { required, minLength: minLength(1) },
  description: {},
};

const v$ = useVuelidate(rules, state);

const onClose = () => emit('close');

const editKanban = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    await store.dispatch('kanbans/update', {
      id: props.selectedResponse.id,
      name: state.name,
      description: state.description,
    });
    useAlert(t('KANBAN_SETTINGS.EDIT.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(error.message || t('KANBAN_SETTINGS.EDIT.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="`${t('KANBAN_SETTINGS.EDIT.TITLE')} - ${selectedResponse.name}`"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="editKanban">
      <woot-input
        v-model="state.name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :label="t('KANBAN_SETTINGS.FORM.NAME.LABEL')"
        :placeholder="t('KANBAN_SETTINGS.FORM.NAME.PLACEHOLDER')"
        :error="v$.name.$error ? t('KANBAN_SETTINGS.FORM.NAME.ERROR') : ''"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <woot-input
        v-model="state.description"
        class="w-full"
        :label="t('KANBAN_SETTINGS.FORM.DESCRIPTION.LABEL')"
        :placeholder="t('KANBAN_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
      />
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="t('KANBAN_SETTINGS.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="t('KANBAN_SETTINGS.FORM.EDIT')"
          :disabled="v$.name.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
